import Throttle from '@javascripts/utils/throttle';
import { PlayerEnvironment } from '@javascripts/player_environment';

export default class VideoInformation {
  static shouldInit(data) {
    return (
      PlayerEnvironment.isDesktopSite() &&
      data.embed() === 'false' &&
      data.isVideo() === true &&
      data.filmTitle() !== ''
    )
  }

  constructor({ tech, data } = {}) {
    this._tech = tech;
    this._data = data;
    this._readyToShow = true;
    this._activeTimestamp = Date.now();
    this._throttleIntervalInMilis = 500;
    this._panelDelayDurationInMilis = this.delayDuration();
    this._panelElement = null;
    this._delayedShowPanelId = null;
    this._userActivityCallback = () => {
      this._activeTimestamp = Throttle.execute(this._activeTimestamp, this._throttleIntervalInMilis, this.userActivityAction.bind(this));
    };
  }

  tech() {
    return this._tech;
  }

  data() {
    return this._data;
  }

  panelElement() {
    return this._panelElement;
  }

  delayDuration() {
    try {
      return this.data().testMode() ? 1000 : 10000;
    } catch(e) {
      return 10000;
    }
  }

  readyToShow() {
    return this._readyToShow;
  }

  init() {
    this.appendVideoInformation();
    this.addPauseListener();
    this.addPlayListener();
    this.addEpisodesListOpenListener();
    this.addEpisodesListCloseListener();
    this.addErrorListener();
  }

  appendVideoInformation() {
    this.tech().el().appendChild(this.createVideoInformationPanel());
  }

  addPauseListener() {
    this.tech().on('pause', this.pauseAction.bind(this));
  }

  pauseAction() {
    if (!this.tech().isFullscreen()) {
      return;
    }
    this.delayedShowPanel();
    this.addMousemoveListener();
    this.addKeypressListener();
    this.addEndedListener();
  }

  addPlayListener() {
    this.tech().on('play', () => {
      this.playAction();
    });
  }

  playAction() {
    this.clearDelayedShowPanel();
    this.hidePanel();
    this.removeEventListener();
  }

  addMousemoveListener() {
    this.tech().on('mousemove', this._userActivityCallback)
  }

  userActivityAction() {
    this.clearDelayedShowPanel();
    this.hidePanel();
    this.delayedShowPanel();
  }

  addKeypressListener() {
    this.tech().el().el().addEventListener('keydown', this._userActivityCallback);
  }

  addEndedListener() {
    this.tech().on('ended', () => {
      this.playAction();
    });
  }

  addEpisodesListOpenListener() {
    this.tech().on('vp-episodes-list-open', () => {
      this._readyToShow = false;
      this.playAction();
    });
  }

  addEpisodesListCloseListener() {
    this.tech().on('vp-episodes-list-close', () => {
      this._readyToShow = true;
    });
  }

  addErrorListener() {
    this.tech().on('error', () => {
      this._readyToShow = false;
      this.playAction();
    });
  }

  removeEventListener() {
    this.tech().off('mousemove', this._userActivityCallback)
    this.tech().el().el().removeEventListener('keydown', this._userActivityCallback);
  }

  delayedShowPanel() {
    this._delayedShowPanelId = setTimeout(this.showPanel.bind(this), this._panelDelayDurationInMilis);
  }

  clearDelayedShowPanel() {
    clearTimeout(this._delayedShowPanelId);
  }

  showPanel() {
    if (this.tech().paused() && this.readyToShow()) {
      this._panelElement.classList.add('is-active');
    }
  }

  hidePanel() {
    this._panelElement.classList.remove('is-active');
  }

  createVideoInformationPanel() {
    this._panelElement= document.createElement('div');
    this._panelElement.classList.add('vp-video-information-panel');
    this._panelElement.appendChild(this.createVideoTitle());
    this._panelElement.appendChild(this.createVideoDescription());
    this._panelElement.appendChild(this.createVideoState());
    return this._panelElement;
  }

  createVideoTitle() {
    const title = document.createElement('div');
    title.classList.add('vp-video-information-title');
    title.textContent = this.data().filmTitle();
    title.style.webkitBoxOrient = 'vertical';
    return title;
  }

  createVideoDescription() {
    const description = document.createElement('div');
    description.classList.add('vp-video-information-description');
    description.textContent = this.data().videoDescription();
    description.style.webkitBoxOrient = 'vertical';
    return description;
  }

  createVideoState() {
    const state = document.createElement('div');
    state.classList.add('vp-video-information-state');
    state.textContent = 'paused';
    return state;
  }
}
